/**
 * use-search-deliverables — Wave 2 Batch G hook for the cross-project
 * deliverable picker.
 *
 * Wraps `GET /api/deliverables/search` with TanStack `useInfiniteQuery`
 * keyed on the search params. The hook does NOT debounce — that's the
 * caller's responsibility (the picker component runs the input value
 * through a 300ms debounce before passing it in here, so the cache key
 * stays stable while the user is typing fast).
 *
 * Response shape (BE wraps in `{ data: ... }`, we strip the wrap so the
 * `pages[i]` shape matches the BE's `SearchDeliverablesPayload`):
 *
 *   {
 *     items: SearchDeliverableResult[],
 *     nextCursor: string | null
 *   }
 *
 * `enabled` is false when `q` is empty — the BE rejects q with min length 1
 * and the picker has no UI to render anyway.
 */

import { useInfiniteQuery } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { DeliverableKind } from '@/api/types-bridge'

/**
 * Match the BE `SearchDeliverableResult` shape. Hand-rolled until the next
 * `pnpm openapi:codegen --from=staging` picks up the search route's schema;
 * once that runs, this collapses to a one-line re-export from
 * `components['schemas']['SearchDeliverableResult']` in `types-bridge.ts`.
 */
export interface SearchDeliverableResult {
  kind: DeliverableKind
  id: string
  title: string
  /** `null` for announcements (no shorthand counter in legacy parity). */
  shorthand: string | null
  projectId: string
  projectCode: string
  projectName: string
  statusName: string
  allowsTimer: boolean
}

export interface SearchDeliverablesPayload {
  items: SearchDeliverableResult[]
  nextCursor: string | null
}

export interface SearchDeliverablesParams {
  /** Free-text query. Empty string disables the hook. */
  q: string
  /** Optional kind filter. */
  kind?: DeliverableKind
  /** Optional projectId filter; defaults to "all caller's projects". */
  projectId?: string
  /** When true, restrict to columns where `allowsTimer = true`. */
  allowsTimerOnly?: boolean
  /** Page size. Default 20, BE max 50. */
  limit?: number
}

export const SEARCH_DELIVERABLES_FAMILY = ['deliverables', 'search'] as const

const SEARCH_DELIVERABLES_QUERY_KEY = (params: SearchDeliverablesParams) =>
  [...SEARCH_DELIVERABLES_FAMILY, params] as const

function buildSearchUrl(params: SearchDeliverablesParams, cursor: string | null): string {
  const search = new URLSearchParams()
  search.set('q', params.q)
  if (params.kind) search.set('kind', params.kind)
  if (params.projectId) search.set('projectId', params.projectId)
  if (params.allowsTimerOnly) search.set('allowsTimerOnly', 'true')
  if (params.limit) search.set('limit', String(params.limit))
  if (cursor) search.set('cursor', cursor)
  return `/api/deliverables/search?${search.toString()}`
}

type SearchCache = InfiniteData<SearchDeliverablesPayload, string | null>

export function useSearchDeliverables(
  params: SearchDeliverablesParams,
  opts: { enabled?: boolean } = {},
) {
  const enabled = opts.enabled ?? params.q.trim().length >= 1

  return useInfiniteQuery<
    SearchDeliverablesPayload,
    Error,
    SearchCache,
    ReturnType<typeof SEARCH_DELIVERABLES_QUERY_KEY>,
    string | null
  >({
    queryKey: SEARCH_DELIVERABLES_QUERY_KEY(params),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient<{ data: SearchDeliverablesPayload }>(
        buildSearchUrl(params, pageParam ?? null),
      )
      return res.data
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    // Search results stay fresh for 30s — typing the same query within
    // that window reuses the cache without refetching, which makes the
    // picker feel snappy.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}
